/**
 * Tela da agenda de contatos.
 *
 * Papel: junta a busca, a grade de contatos paginada pela API, a caixa de
 * diálogo com o formulário e a confirmação de exclusão.
 *
 * Como usar: quem tem apenas `ver` no módulo não recebe o botão de criar do
 * cabeçalho, e a `ListaContatos` não recebe `onNovoContato`, `onEditar` nem
 * `onRemover` — por isso o cartão sai sem "editar" e sem "remover", e a agenda
 * continua inteira à vista. É o 403 da Function, e não esta tela, que protege
 * os dados: esconder o botão evita o caminho sem saída, não a gravação, e uma
 * exclusão recusada mostra a mensagem da API dentro da própria caixa de
 * confirmação, que continua aberta. Com a agenda vazia e sem busca ativa, um
 * segundo botão de criar aparece dentro do próprio bloco de vazio, ao lado do
 * que já fica no cabeçalho — os dois abrem a mesma caixa de diálogo.
 *
 * A busca alcança nome, categoria, e-mail, endereço, observação, o número de
 * cada telefone e o nome de quem atende nele. Ignora acento e pontuação, de modo
 * que `cobioles` encontre `Cobióles` e `(11) 4195` encontre o número que está
 * gravado só com dígitos.
 */
import { type ContatoMailing, acessoAlcanca } from '@concierge/shared';
import { type ChangeEvent, useEffect, useState } from 'react';

import { ErroDaApi } from '@/shared/api';
import { useAcesso } from '@/shared/hooks';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Button } from '@/shared/ui/button';
import { CabecalhoDoModulo } from '@/shared/ui/cabecalho-do-modulo';
import { CampoDeBusca } from '@/shared/ui/campo-de-busca';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Label } from '@/shared/ui/label';
import { FormularioContato } from '../components/formulario-contato';
import { ListaContatos } from '../components/lista-contatos';
import { useContatos, useExcluirContato } from '../hooks/usar-contatos';

/** Quantos contatos cada página traz. */
const POR_PAGINA = 24;

/** Quanto esperar depois da última tecla antes de buscar, em milissegundos. */
const ESPERA_DA_BUSCA = 300;

/** Em que estado a caixa do formulário está: fechada, criando ou editando alguém. */
type PainelAberto =
  { modo: 'fechado' } | { modo: 'novo' } | { modo: 'edicao'; contato: ContatoMailing };

/**
 * Página do Mailing.
 *
 * A busca só vai à API depois de uma pausa na digitação, e volta para a
 * primeira página a cada mudança: continuar na página três de uma busca nova
 * mostraria uma grade vazia para um termo que tem resultados.
 *
 * @returns A tela com busca, grade, paginação, formulário e confirmação.
 */
export function PaginaMailing() {
  const [pagina, setPagina] = useState(1);
  const [textoDaBusca, setTextoDaBusca] = useState('');
  const [busca, setBusca] = useState('');
  const [painel, setPainel] = useState<PainelAberto>({ modo: 'fechado' });
  const [aRemover, setARemover] = useState<ContatoMailing | null>(null);
  const [erroDaRemocao, setErroDaRemocao] = useState<string | null>(null);

  const acesso = useAcesso();
  const podeEditar =
    acesso.data !== undefined && acessoAlcanca(acesso.data, 'mailing', 'editar');

  useEffect(() => {
    // A página volta a 1 junto com a busca, e não a cada tecla: voltar antes
    // dispararia uma consulta da página 1 com o termo antigo, que ninguém pediu.
    const espera = setTimeout(() => {
      setBusca(textoDaBusca.trim());
      setPagina(1);
    }, ESPERA_DA_BUSCA);
    return () => clearTimeout(espera);
  }, [textoDaBusca]);

  const consulta = useContatos({
    pagina,
    porPagina: POR_PAGINA,
    busca: busca === '' ? undefined : busca,
  });
  const excluir = useExcluirContato();

  const total = consulta.data?.total ?? 0;
  const ultimaPagina = Math.max(1, Math.ceil(total / POR_PAGINA));
  const primeiroDaPagina = total === 0 ? 0 : (pagina - 1) * POR_PAGINA + 1;
  const ultimoDaPagina = Math.min(pagina * POR_PAGINA, total);

  /** Guarda o texto digitado; a consulta espera a pausa na digitação. */
  function handleBuscaChange(evento: ChangeEvent<HTMLInputElement>) {
    setTextoDaBusca(evento.target.value);
  }

  /** Fecha a caixa, seja pelo botão, pelo `Esc` ou por clique no véu em volta dela. */
  function handlePainelOpenChange(aberto: boolean) {
    if (!aberto) setPainel({ modo: 'fechado' });
  }

  /** Abre a confirmação de exclusão, limpando a falha da tentativa anterior. */
  function handlePedirRemocao(contato: ContatoMailing) {
    setErroDaRemocao(null);
    setARemover(contato);
  }

  /** Fecha a confirmação de exclusão, com a falha anterior junto. */
  function handleRemocaoOpenChange(aberto: boolean) {
    if (aberto) return;
    setARemover(null);
    setErroDaRemocao(null);
  }

  /** Apaga o contato confirmado e fecha a caixa, se a API aceitar. */
  async function handleConfirmarRemocao() {
    if (aRemover === null) return;
    setErroDaRemocao(null);

    try {
      await excluir.mutateAsync(aRemover.id);
      setARemover(null);
    } catch (erro) {
      setErroDaRemocao(
        erro instanceof ErroDaApi
          ? erro.message
          : 'A exclusão falhou antes de chegar ao servidor.',
      );
    }
  }

  /** Refaz a consulta depois de uma falha. */
  function handleTentarDeNovo() {
    void consulta.refetch();
  }

  return (
    <div>
      <CabecalhoDoModulo
        titulo="Mailing"
        acoes={
          podeEditar ? (
            <Button onClick={() => setPainel({ modo: 'novo' })}>+ Novo contato</Button>
          ) : undefined
        }
      />

      <Label htmlFor="busca-mailing" className="sr-only">
        Buscar por nome, categoria, telefone, e-mail ou endereço
      </Label>
      <CampoDeBusca
        id="busca-mailing"
        placeholder="Buscar por nome, categoria, telefone, e-mail ou endereço..."
        value={textoDaBusca}
        onChange={handleBuscaChange}
        autoComplete="off"
      />

      {consulta.error ? (
        <div className="rounded-md border border-dashed border-border bg-card px-6 py-12 text-center">
          <p className="font-serif text-lg text-foreground">
            Não foi possível carregar a agenda
          </p>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            {consulta.error instanceof ErroDaApi
              ? consulta.error.message
              : 'A consulta falhou antes de chegar ao servidor.'}
          </p>
          <Button
            variant="outline"
            className="mt-4"
            disabled={consulta.isFetching}
            onClick={handleTentarDeNovo}
          >
            {consulta.isFetching ? 'Tentando…' : 'Tentar de novo'}
          </Button>
        </div>
      ) : (
        <>
          <ListaContatos
            contatos={consulta.data?.itens}
            carregando={consulta.isPending}
            mensagemVazia={
              busca === ''
                ? 'Nenhum contato cadastrado ainda.'
                : `Nenhum contato encontrado para "${busca}".`
            }
            onNovoContato={
              busca === '' && podeEditar ? () => setPainel({ modo: 'novo' }) : undefined
            }
            onEditar={
              podeEditar ? (contato) => setPainel({ modo: 'edicao', contato }) : undefined
            }
            onRemover={podeEditar ? handlePedirRemocao : undefined}
          />

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
            <p className="tabular" aria-live="polite">
              {total === 0 ? '' : `${primeiroDaPagina}–${ultimoDaPagina} de ${total}`}
              {consulta.isPlaceholderData && ' · atualizando…'}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagina <= 1 || consulta.isPlaceholderData}
                onClick={() => setPagina((atual) => atual - 1)}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={pagina >= ultimaPagina || consulta.isPlaceholderData}
                onClick={() => setPagina((atual) => atual + 1)}
              >
                Próxima
              </Button>
            </div>
          </div>
        </>
      )}

      <Dialog open={painel.modo !== 'fechado'} onOpenChange={handlePainelOpenChange}>
        <DialogContent>
          {painel.modo !== 'fechado' && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {painel.modo === 'novo' ? 'Novo contato' : painel.contato.nome}
                </DialogTitle>
              </DialogHeader>
              <FormularioContato
                key={painel.modo === 'novo' ? 'novo' : painel.contato.id}
                contato={painel.modo === 'novo' ? null : painel.contato}
                onSalvo={() => setPainel({ modo: 'fechado' })}
                onCancelar={() => setPainel({ modo: 'fechado' })}
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={aRemover !== null} onOpenChange={handleRemocaoOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover {aRemover?.nome}?</AlertDialogTitle>
            <AlertDialogDescription>
              O contato e os telefones dele saem da agenda. Não há como desfazer.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {erroDaRemocao !== null && (
            <p className="text-sm text-destructive" role="alert">
              {erroDaRemocao}
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={excluir.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={excluir.isPending}
              onClick={(evento) => {
                // O `AlertDialogAction` fecha a caixa ao ser clicado, e fechá-la
                // antes da resposta da API desmontaria o estado de envio e
                // esconderia a falha. O fechamento passa a ser nosso, depois de
                // a exclusão confirmar.
                evento.preventDefault();
                void handleConfirmarRemocao();
              }}
            >
              {excluir.isPending ? 'Removendo…' : 'Remover'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
